import { action, computed, observable, runInAction } from 'mobx'
import moment from 'moment-timezone'
import { penceToPounds } from '../../../shared-lib/currency'
import { daysAsString, periodsForDays } from '../../lib/utils'

import { $RootStore } from '../../../src/stores/RootStore'
import { GetGoogleStatsQuery, GetStatsOverviewQuery } from '../../../graphQL/generated/client'

export type StatsOverviewKeys = keyof Omit<GetStatsOverviewQuery['getStatsOverview'], 'averageBookingDuration'> | 'averageBookingHourlyRate'
export type GoogleStatsKeys = keyof Omit<GetGoogleStatsQuery['getGoogleStats']['currentPeriod']['analytics'][number], 'keys'>

export type StatsKeys = GoogleStatsKeys | StatsOverviewKeys
const LIST_STATS_KEYS = ['topKeywords'] as const
export type ListStatsKeys = Extract<(typeof LIST_STATS_KEYS)[number], StatsKeys>

export type StatsSource = 'google' | 'statsOverview'
export type StatsKeysWithSource = `google-${GoogleStatsKeys}` | `statsOverview-${StatsOverviewKeys}`

export type $StatisticsStore = StatisticsStore

export interface GoogleStats {
  seven?: GetGoogleStatsQuery['getGoogleStats']
  thirty?: GetGoogleStatsQuery['getGoogleStats']
  ninety?: GetGoogleStatsQuery['getGoogleStats']
  threeSixtyFive?: GetGoogleStatsQuery['getGoogleStats']
}

export interface StatsOverview {
  seven?: GetStatsOverviewQuery['getStatsOverview']
  thirty?: GetStatsOverviewQuery['getStatsOverview']
  ninety?: GetStatsOverviewQuery['getStatsOverview']
  threeSixtyFive?: GetStatsOverviewQuery['getStatsOverview']
}

export interface IncomeStats {
  [monthAndYear: string]: {
    day: string
    income: number
    bookings: number
  }[]
}

interface AdditionalTextOption {
  text: string
  isPositive: boolean
  type?: 'success' | 'error' | 'default' | 'primary' | 'secondary' | 'info' | 'warning'
  extraText?: string
}

export interface StatList {
  name: string
  fullName: StatsKeysWithSource
  statListItems: string[]
}
export interface Stat {
  name: string
  value: string | number
  statUnit?: string
  difference?: string | number
  moreIsBad?: boolean
  prefix?: string
  additional?: AdditionalTextOption
  fullName: StatsKeysWithSource
  additionalInfo?: string
}

export type PeriodValue = 7 | 30 | 90 | 365

export type Period = {
  label: string
  value: PeriodValue
}

export default class StatisticsStore {
  rootStore: $RootStore
  @observable accessor loading = false
  @observable accessor selectedTime = 7
  @observable accessor selectedMemberId: string | undefined = undefined
  @observable accessor googleStats: GoogleStats = {}
  @observable accessor incomeStats: IncomeStats = {}
  @observable accessor statsOverview: StatsOverview = {}
  refectchAt = moment().startOf('day')
  selectablePeriods: Period[] = [{
    label: 'Last 7 days',
    value: 7,
  }, {
    label: 'Last 30 days',
    value: 30,
  }, {
    label: 'Last 90 days',
    value: 90,
  }, {
    label: 'Last 365 days',
    value: 365,
  }]

  @action setSelectedPeriod = (period: PeriodValue) => {
    this.selectedTime = period
  }

  @action setSelectedMemberId = (memberId: string | undefined) => {
    this.selectedMemberId = memberId
    this.statsOverview = {}
    this.incomeStats = {}
  }

  @action.bound clearData = () => {
    this.refectchAt = moment().startOf('day')
    this.selectedMemberId = undefined
    this.googleStats = {}
    this.incomeStats = {}
    this.statsOverview = {}
  }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  requestData = (hasData: boolean) => {
    const { userStore: { data: { _id } } } = this.rootStore
    const useCache = this.refectchAt.isAfter(moment())
    return {
      variables: { _id, periods: periodsForDays(this.selectedTime) },
      options: { fetchPolicy: hasData && useCache ? 'cache-only' : 'cache-first' } as const,
    }
  }

  @action.bound async getHomeStats() {
    if (this.rootStore.authStore.isReceptionSession) return

    const { homeStats, syncInStorage } = this.rootStore.uiStore

    await this.getStatsOverview_api()

    // remove ss and solo stats
    if (homeStats.some(stat => stat.startsWith('ss') || stat.startsWith('solo'))) {
      homeStats.length = 0
    }

    const noStats = homeStats.length === 0

    runInAction(() => {
      if (noStats) {
        homeStats.push('statsOverview-completedBookings')
        homeStats.push('statsOverview-websiteVisits')
      }
      syncInStorage()
    })
  }

  statUnitForStat = (propertyName: StatsKeys) => {
    const units: Partial<Record<StatsKeys, string>> = {
      ctr: '%',
      repeatBookings: '%',
      websiteConversionRate: '%',
    }
    return units[propertyName] ?? ''
  }

  private getPeriodText = (period: number): string => {
    const periodMap: Record<number, string> = {
      7: 'last 7 days',
      30: 'last month',
      90: 'last 3 months',
      365: 'last year',
    }
    return periodMap[period] ?? ''
  }

  private getStatDescription = (propertyName: StatsKeys): string => {
    const descriptions: Partial<Record<StatsKeys, string>> = {
      clicks: '**Clicks** is how many times a user clicked through to your site.',
      impressions: '**Impressions**  is how many times a user saw a link to your site in search results.',
      position: '**Position**  is the average position of your site in search results, based on its highest position whenever it appeared in a search.',
      ctr: '**Average CTR** is the percentage of impressions that resulted in a click.',
      websiteVisits: '**Total visits** is the total number of people who visited your website.',
      websiteConversionRate: '**Conversion** is the percentage of website visits that resulted in a booking.',
      websiteBookings: '**Bookings** is the number of bookings made through your website.',
      averageBookingValue: '**Avg. Value** is the average value of a booking during this period. This includes any deductions from discounts you offer.',
      averageBookingDiscount: '**Avg. Discount** is the average discount per booking during this period. For example, from promo codes applied to your bookings.',
      averageBookingHourlyRate: '**Avg. hourly rate** is how much you earn per hour of work. This is calculated based on the length of your bookings and does not include travel time or buffer time.',
      completedBookings: '**All bookings** is the the total number of completed bookings in the selected time period.',
      newBookings: '**New bookings** is the number of bookings from first-time clients who have never booked with you before.',
      repeatBookings: '**% Repeat** is the percentage of bookings from returning clients who have booked with you previously.',
    }
    return descriptions[propertyName] ?? ''
  }

  private formatDifferenceText = (propertyName: StatsKeys, differenceText: string, differenceAbsoluteText: string | number, periodText: string): string => {
    const { s, pluralise, currencySymbol } = this.rootStore.i18n
    const units = {
      ctr: '%',
      position: ` ${pluralise('position', Number(differenceAbsoluteText))}`,
      completedBookings: ` ${pluralise('booking', Number(differenceAbsoluteText))}`,
      newBookings: ` ${pluralise('booking', Number(differenceAbsoluteText))}`,
      repeatBookings: '%',
      averageBookingValue: currencySymbol,
      averageBookingDiscount: currencySymbol,
      averageBookingHourlyRate: currencySymbol,
      websiteVisits: '',
      websiteConversionRate: '%',
      websiteBookings: '',
      clicks: ` ${pluralise('click', Number(differenceAbsoluteText))}`,
      impressions: ` ${pluralise('impression', Number(differenceAbsoluteText))}`,
      topKeywords: '',
    }

    return s('$0 **$1$2** compared to the $3', differenceText, differenceAbsoluteText, units[propertyName], periodText)
  }

  additionalInfoForStat = (propertyName: StatsKeys, difference: number | string, period: number) => {
    const { s } = this.rootStore.i18n
    const isMore = typeof difference === 'number' && difference > 0
    const differenceText = isMore ? 'Up' : 'Down'
    const differenceAbsolute = Math.abs(Number(difference))

    const transform = this.transformForStat(propertyName)
    const differenceAbsoluteText = transform ? transform(differenceAbsolute) : differenceAbsolute
    const hasDifference = parseInt(differenceAbsoluteText as string) !== 0

    const description = this.getStatDescription(propertyName)
    const periodText = this.getPeriodText(period)

    if (!hasDifference) {
      return description
    }

    const differenceInfo = this.formatDifferenceText(propertyName, differenceText, differenceAbsoluteText, periodText)
    return s(description + ' \n\n ' + differenceInfo)
  }

  getStat = (propertyName: StatsKeys, { type }: { type: StatsSource }): Stat => {
    const name = this.nameForStat(propertyName, type)
    const prefix = this.prefixForStat(propertyName)
    const fullName = this.fullNameForStat(propertyName, type)
    const moreIsBad = this.checkMoreIsBad(propertyName)
    const statUnit = this.statUnitForStat(propertyName)

    let value: number | string = 0
    let difference: number | string = 0
    let additionalInfo: string = ''

    const period = this.getStatData(type)
    if (period && propertyName in period) {
      const periodData = period[propertyName as keyof typeof period] as PeriodData
      value = periodData.currentPeriod as number
      difference = periodData.currentPeriod - periodData.previousPeriod
      additionalInfo = this.additionalInfoForStat(propertyName, difference, this.selectedTime)
    }

    const transform = this.transformForStat(propertyName)
    if (transform) {
      value = transform(value)
      difference = transform(difference)
    }

    return { value, difference, name, prefix, fullName, moreIsBad, statUnit, additionalInfo }
  }

  getListStat = (propertyName: ListStatsKeys, { type }: { type: StatsSource }): StatList => {
    const name = this.nameForStat(propertyName, type)
    const fullName = this.fullNameForStat(propertyName, type)
    const statData = this.getStatData(type)
    const statListItems = type === 'statsOverview' && statData ? (statData as any)[propertyName] as string[] : []
    return { name, fullName, statListItems: statListItems.slice(0, 3) }
  }

  getStatData = (type: StatsSource) => {
    switch (type) {
      case 'google':
        return this.googleStatsSum
      case 'statsOverview':
        return this.statsOverview[this.timeAgoAsString]
    }
  }

  getStatFromFullName = (fullName: StatsKeysWithSource) => {
    const [source, name] = fullName.split('-') as [StatsSource, StatsKeys]

    if (LIST_STATS_KEYS.includes(name as ListStatsKeys)) {
      return this.getListStat(name as ListStatsKeys, { type: source })
    }

    return this.getStat(name, { type: source })
  }

  checkMoreIsBad = (propertyName: StatsKeys) => {
    const reverseStats = ['position', 'noShows'] as StatsKeys[]
    return reverseStats.includes(propertyName)
  }

  nameForStat = (propertyName: StatsKeys, type: StatsSource) => {
    const names: Partial<Record<StatsKeys, string>> = {
      completedBookings: 'All bookings',
      impressions: 'Impressions',
      clicks: 'Clicks',
      ctr: 'Average CTR',
      position: 'Average Pos.',
      averageBookingValue: 'Avg. value',
      averageBookingDiscount: 'Avg. discount',
      averageBookingHourlyRate: 'Avg. hourly rate',
      websiteVisits: 'Total visits',
      websiteConversionRate: 'Conversion',
      websiteBookings: type === 'statsOverview' ? 'Bookings' : 'Top referrers',
      newBookings: 'New bookings',
      repeatBookings: '% Repeat',
      topKeywords: 'Top keywords',
    }

    return names[propertyName] ?? ''
  }

  prefixForStat = (propertyName: StatsKeys) => {
    const { currencySymbol } = this.rootStore.i18n

    const prefixes: Partial<Record<StatsKeys, string>> = {
      averageBookingHourlyRate: currencySymbol,
      averageBookingDiscount: currencySymbol,
      averageBookingValue: currencySymbol,
    }
    return prefixes[propertyName] ?? ''
  }

  fullNameForStat = (propertyName: StatsKeys, type: StatsSource) => {
    return `${type}-${propertyName}` as StatsKeysWithSource
  }

  transformForStat = (propertyName: StatsKeys) => {
    const transforms: Partial<Record<StatsKeys, (value: number) => string | number>> = {
      ctr: (value: number) => value.toFixed(2),
      position: (value: number) => Number(value.toFixed(0)),
      impressions: (value: number) => this.highValueTransform(value),
      clicks: (value: number) => this.highValueTransform(value),
      averageBookingValue: (value: number) => penceToPounds(value).toFixed(2),
      averageBookingDiscount: (value: number) => penceToPounds(value).toFixed(2),
      averageBookingHourlyRate: (value: number) => penceToPounds(value).toFixed(2),
      websiteVisits: (value: number) => this.highValueTransform(value),
      websiteConversionRate: (value: number) => value.toFixed(2),
      websiteBookings: (value: number) => this.highValueTransform(value),
      repeatBookings: (value: number) => value.toFixed(2),
    }
    return transforms[propertyName]
  }

  highValueTransform = (value: number) => {
    const absValue = Math.abs(value)
    return absValue > 1000000 ? `${(value / 1000000).toFixed(1)}M` : absValue > 1000 ? `${(value / 1000).toFixed(1)}K` : value
  }

  @computed get timeAgoAsString() {
    return daysAsString(this.selectedTime)
  }

  @action.bound setLoading(loading: boolean) {
    this.loading = loading
  }

  @action.bound async getGoogleStats_api(testProfileUrl?: string) {
    const { communication: { requester } } = this.rootStore

    const testProfileUrlParam = this.rootStore.config.name !== 'production' ? (testProfileUrl || 'kanji-japaneseheadspa') : undefined

    const { options } = this.requestData(!!this.googleStats[this.timeAgoAsString])

    const variables = {
      testProfileUrl: testProfileUrlParam,
      periods: periodsForDays(this.selectedTime),
    }

    this.setLoading(true)

    try {
      const response = await requester.getGoogleStats(variables, options)

      runInAction(() => {
        if (response.getGoogleStats) {
          this.googleStats[this.timeAgoAsString] = response.getGoogleStats
        }
      })
    } finally {
      this.setLoading(false)
    }
  }

  @computed get formattedGoogleStats(): Record<GoogleStatsKeys, Stat> {
    return {
      impressions: this.getStat('impressions', { type: 'google' }),
      clicks: this.getStat('clicks', { type: 'google' }),
      ctr: this.getStat('ctr', { type: 'google' }),
      position: this.getStat('position', { type: 'google' }),
    }
  }

  getGoogleSumData = (propertyName: GoogleStatsKeys) => {
    const stats = this.googleStats[this.timeAgoAsString]

    if (!stats) return { currentPeriod: 0, previousPeriod: 0 }

    const sumAnalytics = (period: GoogleStatsResponse) =>
      period.analytics.reduce((sum, a) => sum + a[propertyName], 0)

    return {
      currentPeriod: sumAnalytics(stats.currentPeriod),
      previousPeriod: sumAnalytics(stats.previousPeriod),
    }
  }

  getGoogleAverageData = (propertyName: GoogleStatsKeys) => {
    const stats = this.googleStats[this.timeAgoAsString]

    if (!stats) return { currentPeriod: 0, previousPeriod: 0 }

    const averageAnalytics = (period: GoogleStatsResponse) =>
      period.analytics.reduce((sum, a) => sum + a[propertyName], 0) / period.analytics.length

    const currentPeriod = averageAnalytics(stats.currentPeriod)
    let previousPeriod = averageAnalytics(stats.previousPeriod)

    if (previousPeriod === 0) {
      previousPeriod = currentPeriod
    }

    return { currentPeriod, previousPeriod }
  }

  @computed get googleStatsSum() {
    return {
      impressions: this.getGoogleSumData('impressions'),
      clicks: this.getGoogleSumData('clicks'),
      ctr: this.getGoogleAverageData('ctr'),
      position: this.getGoogleAverageData('position'),
    }
  }

  @computed get selectedTimeGoogleData() {
    return this.googleStats[this.timeAgoAsString]
  }

  getIncomeStats_api = async ({ selectedMonthAndYear, type }: { selectedMonthAndYear: string, type: 'all' | 'solo' }) => {
    const { communication: { requester } } = this.rootStore

    const startDate = moment(selectedMonthAndYear).startOf('month').format('YYYY-MM-DD')
    const endDate = moment(selectedMonthAndYear).endOf('month').format('YYYY-MM-DD')

    const response = await requester.getIncomeStats({
      input: {
        startDate,
        endDate,
        type,
        memberId: this.selectedMemberId,
      },
    })

    runInAction(() => {
      this.incomeStats[selectedMonthAndYear] = response.getIncomeStats
    })

    return response.getIncomeStats
  }

  getStatsOverview_api = async () => {
    const { communication: { requester } } = this.rootStore

    this.setLoading(true)

    const { options } = this.requestData(!!this.statsOverview[this.timeAgoAsString])

    const response = await requester.getStatsOverview({
      periods: periodsForDays(this.selectedTime),
      memberId: this.selectedMemberId,
    }, options)

    runInAction(() => {
      this.statsOverview[this.timeAgoAsString] = response.getStatsOverview
    })

    this.setLoading(false)

    return response.getStatsOverview
  }

  @computed get formattedStatsOverview(): Record<StatsOverviewKeys, Stat | StatList> {
    return {
      websiteVisits: this.getStat('websiteVisits', { type: 'statsOverview' }),
      websiteConversionRate: this.getStat('websiteConversionRate', { type: 'statsOverview' }),
      websiteBookings: this.getStat('websiteBookings', { type: 'statsOverview' }),
      averageBookingValue: this.getStat('averageBookingValue', { type: 'statsOverview' }),
      averageBookingDiscount: this.getStat('averageBookingDiscount', { type: 'statsOverview' }),
      averageBookingHourlyRate: this.getStat('averageBookingHourlyRate', { type: 'statsOverview' }),
      completedBookings: this.getStat('completedBookings', { type: 'statsOverview' }),
      newBookings: this.getStat('newBookings', { type: 'statsOverview' }),
      repeatBookings: this.getStat('repeatBookings', { type: 'statsOverview' }),
      clicks: this.getStat('clicks', { type: 'statsOverview' }),
      impressions: this.getStat('impressions', { type: 'statsOverview' }),
      topKeywords: this.getListStat('topKeywords', { type: 'statsOverview' }),
    }
  }
}
